import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { merge, switchMap } from 'rxjs';
import { CrossFieldErrorStateMatcher } from '../../../validators/schedule-calendar.validators';
import {
  createTimeRangeGroup,
  ITimesControls,
  RangeFormGroup,
} from './schedule-times-fields.form';

/**
 * Fields for the available time and the preferred ranges inside it. Additional
 * errors can be projected below the available time's errors.
 */
@Component({
  selector: 'app-schedule-times-fields',
  templateUrl: './schedule-times-fields.component.html',
  styleUrls: ['./schedule-times-fields.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col gap-3' },
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
  ],
})
export class ScheduleTimesFieldsComponent {
  readonly controls = input.required<ITimesControls>();

  private readonly timeOptions = [
    ...Array.from({ length: 24 }).flatMap((_, i) =>
      Array.from({ length: 4 }).map((_, j) =>
        [i, j * 15]
          .map((component) => component.toString().padStart(2, '0'))
          .join(':')
      )
    ),
    '24:00',
  ];
  readonly startTimeOptions = this.timeOptions.slice(0, -1);
  readonly endTimeOptions = this.timeOptions.slice(1);

  readonly rangeErrorStateMatcher = new CrossFieldErrorStateMatcher(
    'endBeforeStart'
  );

  constructor() {
    const changeDetectorRef = inject(ChangeDetectorRef);

    // The parent can change the controls, e.g. touching them all on submit
    toObservable(this.controls)
      .pipe(
        switchMap(({ time, allPreferred, preferredTimes }) =>
          merge(time.events, allPreferred.events, preferredTimes.events)
        ),
        takeUntilDestroyed()
      )
      .subscribe(() => changeDetectorRef.markForCheck());
  }

  get time() {
    return this.controls().time;
  }

  get preferredTimes() {
    return this.controls().preferredTimes;
  }

  get preferredTimeOptions() {
    if (this.time.invalid) {
      return this.timeOptions;
    }

    const { start, end } = this.time.controls;

    return this.timeOptions.slice(
      this.timeOptions.indexOf(start.value),
      this.timeOptions.indexOf(end.value) + 1
    );
  }

  get isAllPreferred() {
    return this.controls().allPreferred.value;
  }

  addPreferredTimeRange() {
    if (this.isAllPreferred || this.time.hasError('endBeforeStart')) {
      return;
    }

    const { start, end } = this.time.controls;

    this.preferredTimes.push(
      createTimeRangeGroup({
        start: start.value || '09:00',
        end: end.value || '17:00',
      })
    );
  }

  removePreferredTimeRange(index: number) {
    this.preferredTimes.removeAt(index);
  }

  toggleAllPreferred() {
    this.controls().allPreferred.setValue(!this.isAllPreferred);
  }

  getTimeRangeError(range: RangeFormGroup = this.time) {
    return range.touched && range.hasError('endBeforeStart')
      ? 'End time must be after the start time'
      : null;
  }
}
